import { FRET_MARKERS, fretboard, type Tuning } from "../../../theory/fretboard";
import { midiToName } from "../../../theory/notes";

export interface FretboardProps {
  tuning: Tuning;
  frets: number;
  /** Pitch class → spelled name, for the notes to show. */
  highlight: ReadonlyMap<number, string>;
  rootPc: number;
  onPlay: (midi: number) => void;
}

/**
 * Fretboard diagram: highest string on top (as players read tab). Only
 * highlighted positions are buttons, so Tab moves through notes, not empty frets.
 */
export function Fretboard({ tuning, frets, highlight, rootPc, onPlay }: FretboardProps) {
  const board = fretboard(tuning, frets);
  const rows = [...board].reverse();
  return (
    <div className="fretboard" role="group" aria-label={`Fretboard: ${tuning.label}`} style={{ ["--frets" as string]: frets + 1 }}>
      {rows.map((cells) => {
        const open = cells[0]!;
        const stringName = midiToName(open.midi);
        return (
          <div className="fb-row" key={open.string}>
            <span className="fb-string" aria-hidden="true">{stringName}</span>
            {cells.map((c) => {
              const name = highlight.get(c.pc);
              return (
                <span key={c.fret} className={`fb-cell ${c.fret === 0 ? "open" : ""}`}>
                  {name && (
                    <button
                      type="button" className={`fb-note ${c.pc === rootPc ? "root" : ""}`}
                      aria-label={`${stringName} string, ${c.fret === 0 ? "open" : `fret ${c.fret}`}: ${name}${c.pc === rootPc ? " (root)" : ""}`}
                      onClick={() => onPlay(c.midi)}
                    >
                      {name}
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        );
      })}
      <div className="fb-row fb-numbers" aria-hidden="true">
        <span className="fb-string" />
        {Array.from({ length: frets + 1 }, (_, f) => <span key={f} className="fb-cell">{f === 0 ? "" : FRET_MARKERS.has(f) ? `${f}•` : f}</span>)}
      </div>
    </div>
  );
}
